import {
    areShapesEquivalent,
    enumerateShapeItems,
    extractEdgeVertices,
    resolveEdgeSelections,
    type ResolvedTopologySelection,
} from './common';
import { filterActivePartialEdgeParams } from './partial-edge-activation';

type PuppyKernel = ReturnType<typeof import('../../../engine/loader').getPuppyKernel>;
type WorldPoint = [number, number, number];

type PartialRange = {
    startDistance: number;
    endDistance: number;
};

type LinearEdgeDescriptor = {
    selection: ResolvedTopologySelection['selection'];
    edgeShape: unknown;
    startPoint: WorldPoint;
    direction: WorldPoint;
    length: number;
    range: PartialRange;
};

type DisposableShape = {
    delete?: () => void;
};

type SplitPlaneTool = {
    face: unknown;
    dispose: () => void;
};

export interface PartialEdgeBlendTarget {
    selection: SelectionItemRef;
    edgeShape: unknown;
}

export interface PartialEdgeBlendSolidGroup {
    solidShape: unknown;
    edgeTargets: PartialEdgeBlendTarget[];
}

export interface PartialEdgeBlendTargets {
    targetShape: unknown;
    edgeTargets: PartialEdgeBlendTarget[];
    solidGroups: PartialEdgeBlendSolidGroup[];
    remainderSolidShapes: unknown[];
    requiresSameDomainUnification: boolean;
    cleanup: () => void;
}

type ResolvePartialEdgeBlendTargetsInput = {
    puppy: PuppyKernel;
    sourceShape: unknown;
    sourceMetadata: Record<string, unknown>;
    items: SelectionItemRef[] | undefined;
    partialEdges?: PartialEdgeParam[];
    sourceObjectId: string;
    operationLabel: string;
};

const EDGE_TOLERANCE = 1e-6;
const RANGE_TOLERANCE = 1e-4;

function safeDelete(value: unknown): void {
    const deleteMethod = (value as DisposableShape | null)?.delete;
    if (typeof deleteMethod !== 'function') {
        return;
    }
    try {
        deleteMethod.call(value);
    } catch {
        // 中间 Puppy CAD 内核 对象释放失败不应覆盖真正的求解错误。
    }
}

/**
 * 在未启用局部边参数时，直接解析整边目标集合。
 *
 * 设计说明：
 * 1. constant fillet/chamfer 的常规主链并不需要把每条边先投喂给“直线局部分段器”；
 * 2. 如果没有任何真正激活的 `partialEdges`，继续走线性描述符只会把曲线传播边误杀，并且平白增加一次 Puppy CAD 内核 曲线检查成本；
 * 3. 因此这里直接以权威拓扑解析结果构建整边目标，既修复曲线切线传播，又缩短普通等半径圆角的热路径。
 *
 * @param input 局部边目标解析输入。
 * @returns 直接整边目标集合。
 */
function resolveWholeEdgeBlendTargets(
    input: ResolvePartialEdgeBlendTargetsInput,
): PartialEdgeBlendTargets {
    const selections = resolveEdgeSelections(
        input.puppy,
        input.sourceShape,
        input.sourceMetadata,
        input.items ?? [],
        { sourceObjectId: input.sourceObjectId },
    );
    const edgeTargets = selections.map(({ selection, edgeShape }) => ({ selection, edgeShape }));
    return {
        targetShape: input.sourceShape,
        edgeTargets,
        solidGroups: [{
            solidShape: input.sourceShape,
            edgeTargets,
        }],
        remainderSolidShapes: [],
        requiresSameDomainUnification: false,
        cleanup: () => { },
    };
}

function subtract(left: WorldPoint, right: WorldPoint): WorldPoint {
    return [left[0] - right[0], left[1] - right[1], left[2] - right[2]];
}

function add(left: WorldPoint, right: WorldPoint): WorldPoint {
    return [left[0] + right[0], left[1] + right[1], left[2] + right[2]];
}

function scale(vector: WorldPoint, factor: number): WorldPoint {
    return [vector[0] * factor, vector[1] * factor, vector[2] * factor];
}

function dot(left: WorldPoint, right: WorldPoint): number {
    return (left[0] * right[0]) + (left[1] * right[1]) + (left[2] * right[2]);
}

function length(vector: WorldPoint): number {
    return Math.sqrt(dot(vector, vector));
}

function cross(left: WorldPoint, right: WorldPoint): WorldPoint {
    return [
        (left[1] * right[2]) - (left[2] * right[1]),
        (left[2] * right[0]) - (left[0] * right[2]),
        (left[0] * right[1]) - (left[1] * right[0]),
    ];
}

function normalize(vector: WorldPoint, operationLabel: string): WorldPoint {
    const vectorLength = length(vector);
    if (vectorLength <= EDGE_TOLERANCE) {
        throw new Error(`${operationLabel} 无法在退化边上计算方向`);
    }
    return [
        vector[0] / vectorLength,
        vector[1] / vectorLength,
        vector[2] / vectorLength,
    ];
}

function interpolate(startPoint: WorldPoint, direction: WorldPoint, distance: number): WorldPoint {
    return add(startPoint, scale(direction, distance));
}

function projectDistanceOnLine(point: WorldPoint, origin: WorldPoint, direction: WorldPoint): number {
    return dot(subtract(point, origin), direction);
}

function distanceToLine(point: WorldPoint, origin: WorldPoint, direction: WorldPoint): number {
    return length(cross(subtract(point, origin), direction));
}

function assertStraightEdge(
    puppy: PuppyKernel,
    edgeShape: unknown,
    startPoint: WorldPoint,
    endPoint: WorldPoint,
    operationLabel: string,
): void {
    const curveCtor = (puppy as Record<string, unknown>).BRepAdaptor_Curve_2 as
        | (new (edge: unknown) => {
            FirstParameter: () => number;
            LastParameter: () => number;
            Value: (parameter: number) => { X: () => number; Y: () => number; Z: () => number };
            delete?: () => void;
        })
        | undefined;
    if (typeof curveCtor !== 'function') {
        return;
    }

    const curve = new curveCtor(edgeShape);
    try {
        const firstParameter = curve.FirstParameter();
        const lastParameter = curve.LastParameter();
        const midpoint = curve.Value((firstParameter + lastParameter) * 0.5);
        const middlePoint: WorldPoint = [midpoint.X(), midpoint.Y(), midpoint.Z()];
        const lineDirection = normalize(subtract(endPoint, startPoint), operationLabel);
        if (distanceToLine(middlePoint, startPoint, lineDirection) > RANGE_TOLERANCE) {
            throw new Error(`当前 ${operationLabel} solver 仅支持直线边主链路`);
        }
    } finally {
        safeDelete(curve);
    }
}

function resolvePercentageOffsetDistance(
    rawValue: number | undefined,
    edgeLength: number,
    operationLabel: string,
): number {
    const percent = rawValue ?? 0;
    if (percent < 0 || percent > 100) {
        throw new Error(`${operationLabel} 的 percentage-offset 值必须位于 0 到 100 之间`);
    }
    /**
     * 百分比偏移本质上仍然是“从当前端点量到分割位置”的距离，
     * 在 cad-core 统一换算成绝对边长，避免 UI、preview 和 execute 各自解释。
     */
    return edgeLength * (percent / 100);
}

/**
 * reference-offset 解析所需的边几何上下文。
 * 仅包含目标边的线性参数，参考点会直接投影到这条直线上。
 */
type ReferenceOffsetContext = {
    edgeStartPoint: WorldPoint;
    edgeDirection: WorldPoint;
    edgeLength: number;
};

/**
 * 将参考点投影到目标边，得到从边起点量起的绝对距离。
 *
 * 设计说明：
 * 1. 参考等距在当前项目中只保留参考点语义，不再叠加开始值/终止值；
 * 2. 该函数作为 worker 纯几何入口暴露，方便用回归测试锁住“旧数值不参与求解”的底层行为；
 * 3. 投影结果统一钳制到目标边长度范围内，避免外部草图点落在边延长线上时产生非法切割范围。
 *
 * @param input 参考点投影输入。
 * @returns 从边起点量起的绝对距离。
 */
export function projectBlendPointRefToEdgeDistance(
    input: ReferenceOffsetContext & {
        referencePoint: BlendPointRef | null | undefined;
        operationLabel: string;
    },
): number {
    if (!input.referencePoint) {
        throw new Error(`${input.operationLabel} 的 reference-offset 条件缺少参考点`);
    }
    return Math.max(
        0,
        Math.min(
            input.edgeLength,
            projectDistanceOnLine(input.referencePoint.point, input.edgeStartPoint, input.edgeDirection),
        ),
    );
}

function resolveReferenceOffsetDistance(
    context: ReferenceOffsetContext,
    referencePoint: BlendPointRef | null | undefined,
    operationLabel: string,
): number {
    return projectBlendPointRefToEdgeDistance({
        ...context,
        referencePoint,
        operationLabel,
    });
}

function resolveStartDistance(
    partial: PartialEdgeParam | null,
    edgeLength: number,
    operationLabel: string,
    referenceContext?: ReferenceOffsetContext,
): number {
    if (!partial) {
        return 0;
    }
    switch (partial.startCondition) {
        case 'none':
        case 'vertex':
            return 0;
        case 'offset':
            return Math.max(0, partial.startValue ?? 0);
        case 'percentage-offset':
            return resolvePercentageOffsetDistance(partial.startValue, edgeLength, operationLabel);
        case 'reference-offset':
            if (!referenceContext) {
                throw new Error(`${operationLabel} 的 reference-offset 条件缺少几何上下文`);
            }
            return Math.max(0, resolveReferenceOffsetDistance(
                referenceContext,
                partial.startReferencePoint,
                operationLabel,
            ));
        default:
            return 0;
    }
}

function resolveEndDistance(
    partial: PartialEdgeParam | null,
    edgeLength: number,
    operationLabel: string,
    referenceContext?: ReferenceOffsetContext,
): number {
    if (!partial) {
        return edgeLength;
    }
    switch (partial.endCondition) {
        case 'none':
        case 'vertex':
            return edgeLength;
        case 'offset':
            return Math.max(0, edgeLength - Math.max(0, partial.endValue ?? 0));
        case 'percentage-offset':
            return Math.max(0, edgeLength - resolvePercentageOffsetDistance(partial.endValue, edgeLength, operationLabel));
        case 'reference-offset': {
            if (!referenceContext) {
                throw new Error(`${operationLabel} 的 reference-offset 条件缺少几何上下文`);
            }
            return Math.max(0, resolveReferenceOffsetDistance(
                referenceContext,
                partial.endReferencePoint,
                operationLabel,
            ));
        }
        default:
            return edgeLength;
    }
}

function resolvePartialRange(
    partial: PartialEdgeParam | null,
    edgeLength: number,
    operationLabel: string,
    referenceContext?: ReferenceOffsetContext,
): PartialRange {
    const startDistance = resolveStartDistance(partial, edgeLength, operationLabel, referenceContext);
    const endDistance = resolveEndDistance(partial, edgeLength, operationLabel, referenceContext);
    if (
        startDistance < 0
        || endDistance < 0
        || startDistance > edgeLength + RANGE_TOLERANCE
        || endDistance > edgeLength + RANGE_TOLERANCE
    ) {
        throw new Error(`${operationLabel} 的起止距离超出了边长范围`);
    }
    if ((endDistance - startDistance) <= EDGE_TOLERANCE) {
        throw new Error(`${operationLabel} 的起止范围过短，无法生成有效结果`);
    }
    return { startDistance, endDistance };
}

function createLinearEdgeDescriptor(
    puppy: PuppyKernel,
    selection: ResolvedTopologySelection,
    partial: PartialEdgeParam | null,
    operationLabel: string,
): LinearEdgeDescriptor {
    const edgeVertices = extractEdgeVertices(puppy, selection.edgeShape);
    if (edgeVertices.length < 2) {
        throw new Error(`${operationLabel} 无法解析边 ${selection.selection.persistentRef} 的端点`);
    }
    const startPoint = edgeVertices[0]!;
    const endPoint = edgeVertices[1]!;
    assertStraightEdge(puppy, selection.edgeShape, startPoint, endPoint, operationLabel);
    const direction = normalize(subtract(endPoint, startPoint), operationLabel);
    const edgeLength = length(subtract(endPoint, startPoint));

    const needsReferenceContext = partial !== null
        && (partial.startCondition === 'reference-offset' || partial.endCondition === 'reference-offset');
    const referenceContext: ReferenceOffsetContext | undefined = needsReferenceContext
        ? { edgeStartPoint: startPoint, edgeDirection: direction, edgeLength }
        : undefined;

    return {
        selection: selection.selection,
        edgeShape: selection.edgeShape,
        startPoint,
        direction,
        length: edgeLength,
        range: resolvePartialRange(partial, edgeLength, operationLabel, referenceContext),
    };
}

function createPlaneFace(
    puppy: PuppyKernel,
    point: WorldPoint,
    normal: WorldPoint,
    operationLabel: string,
): SplitPlaneTool {
    const planePoint = new puppy.gp_Pnt_3(point[0], point[1], point[2]);
    const planeDirection = new puppy.gp_Dir_5(normal[0], normal[1], normal[2]);
    const plane = new puppy.gp_Pln_3(planePoint, planeDirection);
    const faceMaker = new puppy.BRepBuilderAPI_MakeFace_3(plane);
    const isDone = faceMaker.IsDone as (() => boolean) | undefined;
    if (typeof isDone === 'function' && !isDone.call(faceMaker)) {
        safeDelete(faceMaker);
        safeDelete(plane);
        safeDelete(planeDirection);
        safeDelete(planePoint);
        throw new Error(`${operationLabel} 分割平面构造失败`);
    }
    const faceGetter = faceMaker.Face as (() => unknown) | undefined;
    const face = typeof faceGetter === 'function' ? faceGetter.call(faceMaker) : null;
    if (!face) {
        safeDelete(faceMaker);
        safeDelete(plane);
        safeDelete(planeDirection);
        safeDelete(planePoint);
        throw new Error(`${operationLabel} 未生成有效分割面`);
    }
    return {
        face,
        dispose: () => {
            safeDelete(face);
            safeDelete(faceMaker);
            safeDelete(plane);
            safeDelete(planeDirection);
            safeDelete(planePoint);
        },
    };
}

function splitShapeByPlaneFaces(
    puppy: PuppyKernel,
    sourceShape: unknown,
    tools: SplitPlaneTool[],
    operationLabel: string,
): { shape: unknown; cleanup: () => void } {
    if (tools.length === 0) {
        return { shape: sourceShape, cleanup: () => undefined };
    }

    const splitterCtor = ((puppy as Record<string, unknown>).BRepAlgoAPI_Splitter_1
        ?? (puppy as Record<string, unknown>).BRepAlgoAPI_Splitter) as
        | (new () => {
            SetArguments: (argumentsList: { Append: (shape: unknown) => void }) => void;
            SetTools: (toolsList: { Append: (shape: unknown) => void }) => void;
            Build: (progress?: unknown) => void;
            HasErrors?: () => boolean;
            Shape: () => unknown;
            delete?: () => void;
        })
        | undefined;
    const listCtor = (puppy as Record<string, unknown>).NCollection_ListOfShape as
        | (new () => { Append: (shape: unknown) => void; delete?: () => void })
        | undefined;
    if (typeof splitterCtor !== 'function' || typeof listCtor !== 'function') {
        tools.forEach((tool) => tool.dispose());
        throw new Error(`当前 Puppy CAD 内核 绑定缺少 ${operationLabel} 所需的 Splitter 能力`);
    }

    const splitter = new splitterCtor();
    const argumentsList = new listCtor();
    const toolsList = new listCtor();
    const progressCtor = (puppy as Record<string, unknown>).Message_ProgressRange_1 as (new () => unknown) | undefined;
    const progress = typeof progressCtor === 'function' ? new progressCtor() : undefined;

    argumentsList.Append(sourceShape);
    tools.forEach((tool) => toolsList.Append(tool.face));
    splitter.SetArguments(argumentsList);
    splitter.SetTools(toolsList);
    splitter.Build(progress);
    if (typeof splitter.HasErrors === 'function' && splitter.HasErrors()) {
        tools.forEach((tool) => tool.dispose());
        safeDelete(progress);
        safeDelete(toolsList);
        safeDelete(argumentsList);
        safeDelete(splitter);
        throw new Error(`${operationLabel} 在分割实体时失败`);
    }

    const splitShape = splitter.Shape();
    if (!splitShape) {
        tools.forEach((tool) => tool.dispose());
        safeDelete(progress);
        safeDelete(toolsList);
        safeDelete(argumentsList);
        safeDelete(splitter);
        throw new Error(`${operationLabel} 未得到分割后的实体`);
    }

    return {
        shape: splitShape,
        cleanup: () => {
            tools.forEach((tool) => tool.dispose());
            safeDelete(splitShape);
            safeDelete(progress);
            safeDelete(toolsList);
            safeDelete(argumentsList);
            safeDelete(splitter);
        },
    };
}

function resolveEdgeScore(
    edgeVertices: WorldPoint[],
    descriptor: LinearEdgeDescriptor,
): number | null {
    if (edgeVertices.length < 2) {
        return null;
    }

    const projected = edgeVertices.map((point) => projectDistanceOnLine(point, descriptor.startPoint, descriptor.direction));
    const lineDistances = edgeVertices.map((point) => distanceToLine(point, descriptor.startPoint, descriptor.direction));
    if (lineDistances.some((value) => value > RANGE_TOLERANCE)) {
        return null;
    }

    const edgeStart = Math.max(0, Math.min(...projected));
    const edgeEnd = Math.min(descriptor.length, Math.max(...projected));
    const wantedStart = descriptor.range.startDistance;
    const wantedEnd = descriptor.range.endDistance;
    const midpoint = (wantedStart + wantedEnd) * 0.5;
    if (midpoint < edgeStart - RANGE_TOLERANCE || midpoint > edgeEnd + RANGE_TOLERANCE) {
        return null;
    }

    const actualLength = edgeEnd - edgeStart;
    const wantedLength = wantedEnd - wantedStart;
    return Math.abs(edgeStart - wantedStart)
        + Math.abs(edgeEnd - wantedEnd)
        + Math.abs(actualLength - wantedLength);
}

function resolveSegmentEdgeOnShape(
    puppy: PuppyKernel,
    targetShape: unknown,
    descriptor: LinearEdgeDescriptor,
    operationLabel: string,
): unknown {
    const candidateEdges = enumerateShapeItems(puppy, targetShape, 'edge');
    let bestEdge: unknown = null;
    let bestScore = Number.POSITIVE_INFINITY;

    candidateEdges.forEach((candidateEdge) => {
        const candidateVertices = extractEdgeVertices(puppy, candidateEdge);
        const score = resolveEdgeScore(candidateVertices, descriptor);
        if (score === null || score >= bestScore) {
            return;
        }
        bestScore = score;
        bestEdge = candidateEdge;
    });

    if (!bestEdge) {
        throw new Error(`${operationLabel} 无法在分割后的实体上定位边段 ${descriptor.selection.persistentRef}`);
    }
    return bestEdge;
}

function enumerateSolidShapes(puppy: PuppyKernel, shape: unknown): unknown[] {
    const explorerCtor = (puppy as Record<string, unknown>).TopExp_Explorer_2 as
        | (new (shapeArg: unknown, kindArg: unknown, stopKindArg: unknown) => {
            More: () => boolean;
            Current: () => unknown;
            Next: () => void;
            delete?: () => void;
        })
        | undefined;
    const topAbs = (puppy as Record<string, unknown>).TopAbs_ShapeEnum as Record<string, unknown> | undefined;
    const solidWrapper = (puppy as Record<string, unknown>).TopoDS as Record<string, ((shapeArg: unknown) => unknown)> | undefined;
    const solidToken = topAbs?.TopAbs_SOLID;
    if (!shape || !explorerCtor || !topAbs || solidToken === undefined) {
        return [];
    }

    const wrapSolid = solidWrapper?.Solid;
    const explorer = new explorerCtor(shape, solidToken, topAbs.TopAbs_SHAPE);
    const solids: unknown[] = [];
    try {
        while (explorer.More()) {
            const current = explorer.Current();
            solids.push(typeof wrapSolid === 'function' ? wrapSolid(current) : current);
            explorer.Next();
        }
    } finally {
        safeDelete(explorer);
    }
    return solids;
}

function resolveSolidGroups(
    puppy: PuppyKernel,
    targetShape: unknown,
    edgeTargets: PartialEdgeBlendTarget[],
    operationLabel: string,
): {
    solidGroups: PartialEdgeBlendSolidGroup[];
    remainderSolidShapes: unknown[];
} {
    const solidShapes = enumerateSolidShapes(puppy, targetShape);
    if (solidShapes.length === 0) {
        return {
            solidGroups: [{
                solidShape: targetShape,
                edgeTargets,
            }],
            remainderSolidShapes: [],
        };
    }

    const groupEntries = edgeTargets.reduce<Map<number, PartialEdgeBlendSolidGroup>>((groups, edgeTarget) => {
        const matchedSolidIndex = solidShapes.findIndex((solidShape) => (
            enumerateShapeItems(puppy, solidShape, 'edge').some((solidEdge) => (
                areShapesEquivalent(solidEdge, edgeTarget.edgeShape)
            ))
        ));
        if (matchedSolidIndex < 0) {
            throw new Error(`${operationLabel} 无法定位边段 ${edgeTarget.selection.persistentRef} 所属的实体分段`);
        }
        const currentGroup = groups.get(matchedSolidIndex);
        if (currentGroup) {
            groups.set(matchedSolidIndex, {
                ...currentGroup,
                edgeTargets: [...currentGroup.edgeTargets, edgeTarget],
            });
            return groups;
        }
        groups.set(matchedSolidIndex, {
            solidShape: solidShapes[matchedSolidIndex]!,
            edgeTargets: [edgeTarget],
        });
        return groups;
    }, new Map<number, PartialEdgeBlendSolidGroup>());

    const groupedIndexes = new Set(groupEntries.keys());
    return {
        solidGroups: Array.from(groupEntries.entries())
            .sort(([leftIndex], [rightIndex]) => leftIndex - rightIndex)
            .map(([, group]) => group),
        remainderSolidShapes: solidShapes.filter((_shape, index) => !groupedIndexes.has(index)),
    };
}

export function resolvePartialEdgeBlendTargets(
    input: ResolvePartialEdgeBlendTargetsInput,
): PartialEdgeBlendTargets {
    const activePartialEdges = filterActivePartialEdgeParams(input.partialEdges);
    if (activePartialEdges.length === 0) {
        return resolveWholeEdgeBlendTargets(input);
    }

    const selections = resolveEdgeSelections(
        input.puppy,
        input.sourceShape,
        input.sourceMetadata,
        input.items ?? [],
        { sourceObjectId: input.sourceObjectId },
    );
    const partialByRef = new Map(activePartialEdges.map((item) => [item.itemRef, item] as const));
    const descriptors = selections.map((selection) => (
        createLinearEdgeDescriptor(
            input.puppy,
            selection,
            partialByRef.get(selection.selection.persistentRef) ?? null,
            input.operationLabel,
        )
    ));

    const planeTools = descriptors.flatMap((descriptor) => {
        const tools: SplitPlaneTool[] = [];
        if (descriptor.range.startDistance > RANGE_TOLERANCE) {
            tools.push(createPlaneFace(
                input.puppy,
                interpolate(descriptor.startPoint, descriptor.direction, descriptor.range.startDistance),
                descriptor.direction,
                input.operationLabel,
            ));
        }
        if (descriptor.range.endDistance < (descriptor.length - RANGE_TOLERANCE)) {
            tools.push(createPlaneFace(
                input.puppy,
                interpolate(descriptor.startPoint, descriptor.direction, descriptor.range.endDistance),
                descriptor.direction,
                input.operationLabel,
            ));
        }
        return tools;
    });

    const splitResult = splitShapeByPlaneFaces(
        input.puppy,
        input.sourceShape,
        planeTools,
        input.operationLabel,
    );
    if (splitResult.shape === input.sourceShape) {
        const edgeTargets = selections.map(({ selection, edgeShape }) => ({ selection, edgeShape }));
        return {
            targetShape: input.sourceShape,
            edgeTargets,
            solidGroups: [{
                solidShape: input.sourceShape,
                edgeTargets,
            }],
            remainderSolidShapes: [],
            requiresSameDomainUnification: false,
            cleanup: splitResult.cleanup,
        };
    }

    const edgeTargets = descriptors.map((descriptor) => ({
        selection: descriptor.selection,
        edgeShape: resolveSegmentEdgeOnShape(
            input.puppy,
            splitResult.shape,
            descriptor,
            input.operationLabel,
        ),
    }));
    const { solidGroups, remainderSolidShapes } = resolveSolidGroups(
        input.puppy,
        splitResult.shape,
        edgeTargets,
        input.operationLabel,
    );
    return {
        targetShape: splitResult.shape,
        edgeTargets,
        solidGroups,
        remainderSolidShapes,
        requiresSameDomainUnification: true,
        cleanup: splitResult.cleanup,
    };
}
